// Right-click context menu for the design canvas. Rendered via portal at the
// pointer; contextual items per selection type. All actions route through the
// editor API (history commits are the editor's job — see useCanvasEditor).
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
// Smart Tools epic: right-click a smart object → "Tool options…" reopens the
// SmartToolModal (owner 2026-07-23: the modal must always be reachable).
import { openSmartToolModal } from './smartToolModalController';

const MENU_WIDTH = 200;

export default function ContextMenu({ editor, menu, onClose }) {
  const { t } = useTranslation('editor');
  const ref = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const onDocDown = (ev) => {
      if (ref.current && !ref.current.contains(ev.target)) onClose();
    };
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menu, onClose]);

  if (!menu || !editor) return null;

  const selected = editor.selectedObjects || [];
  const hasTarget = menu.hasTarget && selected.length > 0;
  const isMulti = selected.length > 1;
  const isGroup = selected.length === 1 && selected[0]?.type === 'group';
  const isLocked = selected.length > 0 && selected.every((o) => o.locked === true);
  const isPhoto = selected.length === 1 && selected[0]?.type === 'image' && !selected[0]?.isIcon;

  const run = (fn) => () => {
    fn();
    onClose();
  };

  const Item = ({ label, hint, onClick, danger }) => (
    <button
      type="button"
      className={`cce-ctx-item${danger ? ' danger' : ''}`}
      onClick={onClick}
    >
      <span>{label}</span>
      {hint && <span className="cce-ctx-hint">{hint}</span>}
    </button>
  );

  const Divider = () => <div className="cce-ctx-divider" />;

  // Clamp to viewport
  const left = Math.min(menu.x, window.innerWidth - MENU_WIDTH - 8);
  const estHeight = hasTarget ? 320 : 130;
  const top = Math.min(menu.y, window.innerHeight - estHeight - 8);

  return createPortal(
    <div className="cce-ctx-menu" style={{ left, top }} ref={ref} role="menu">
      {hasTarget ? (
        <>
          {!!editor.getSelectedSmartObject?.()?.recipe && (
            <>
              <Item
                label="Tool options…"
                onClick={run(() => openSmartToolModal({ mode: 'edit' }))}
              />
              <Divider />
            </>
          )}
          <Item label={t('context.copy')} hint="Ctrl+C" onClick={run(() => editor.onCopy())} />
          <Item label={t('context.paste')} hint="Ctrl+V" onClick={run(() => editor.onPaste())} />
          <Item label={t('context.duplicate')} hint="Ctrl+D" onClick={run(() => editor.duplicate())} />
          <Item label={t('common:actions.delete')} hint="Del" danger onClick={run(() => { editor.delete(); editor.commit(); })} />
          <Divider />
          <Item label={t('context.bringForward')} onClick={run(() => editor.bringForward())} />
          <Item label={t('context.sendBackward')} onClick={run(() => editor.sendBackwards())} />
          <Item label={t('context.bringToFront')} onClick={run(() => { editor.moveLayerToTop(selected[0]); })} />
          <Item label={t('context.sendToBack')} onClick={run(() => { editor.moveLayerToBottom(selected[0]); })} />
          <Divider />
          <Item
            label={isLocked ? t('context.unlock') : t('context.lock')}
            onClick={run(() => editor.toggleLock())}
          />
          {isPhoto && (
            <Item
              label={t('context.setAsBackground')}
              onClick={run(() => editor.setAsBackground())}
            />
          )}
          {isMulti && <Item label={t('context.group')} onClick={run(() => editor.group())} />}
          {isMulti && (
            <>
              <Item label={t('context.alignLeft')} onClick={run(() => editor.alignSelection('left'))} />
              <Item label={t('context.alignCenter')} onClick={run(() => editor.alignSelection('centerH'))} />
              <Item label={t('context.distributeH')} onClick={run(() => editor.distribute('h'))} />
              <Item label={t('context.distributeV')} onClick={run(() => editor.distribute('v'))} />
            </>
          )}
          {isGroup && <Item label={t('context.ungroup')} onClick={run(() => editor.ungroup())} />}
        </>
      ) : (
        <>
          <Item label={t('context.paste')} hint="Ctrl+V" onClick={run(() => editor.onPaste())} />
          <Item label={t('context.selectAll')} hint="Ctrl+A" onClick={run(() => editor.selectAll())} />
          <Item label={t('context.fitToScreen')} hint="Ctrl+0" onClick={run(() => editor.autoZoom())} />
        </>
      )}
    </div>,
    document.body,
  );
}
